import { useEffect, useRef, useState } from "react";
import api, { formatError, pollProject } from "@/lib/api";
import { toast } from "sonner";

export default function DraftView({ project, onUpdate, onNext }) {
  const [working, setWorking] = useState(false);
  const [view, setView] = useState("libro");
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const started = useRef(false);
  const truncNotified = useRef(false);
  const previewLoaded = useRef(false);

  const waitForDraft = async () => {
    setWorking(true);
    setError(null);
    try {
      const data = await pollProject(project.id, (p) => !p.processing);
      onUpdate(data);
      if (data.process_error) setError(data.process_error);
    } catch (e) {
      setError("Algo no ha cargado. Vuelve a intentarlo; tu trabajo sigue aquí.");
    } finally {
      setWorking(false);
    }
  };

  const runAnalyze = async () => {
    setWorking(true);
    setError(null);
    try {
      await api.post(`/projects/${project.id}/analyze`);
      await waitForDraft();
    } catch (e) {
      setWorking(false);
      setError(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (project.status === "created" && !project.processing) runAnalyze();
    else if (project.processing === "analyzing") waitForDraft();
    // eslint-disable-next-line
  }, []);

  const chapters = project.draft_chapters || [];
  const hasDraft = chapters.length > 0;

  const loadPreview = async () => {
    if (previewLoaded.current || previewLoading) return;
    previewLoaded.current = true;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const { data } = await api.get(`/projects/${project.id}/preview`, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(data));
    } catch (e) {
      setPreviewError("No hemos podido cargar la vista del libro. Inténtalo de nuevo.");
      previewLoaded.current = false;
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (view === "libro" && hasDraft) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, hasDraft]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  useEffect(() => {
    if (hasDraft && project.manuscript_truncated && !truncNotified.current) {
      truncNotified.current = true;
      toast("Hemos analizado las primeras ~40.000 letras de tu texto. Tu material completo se guarda íntegro.");
    }
  }, [hasDraft, project.manuscript_truncated]);

  if (working || project.processing === "analyzing" || (!hasDraft && project.status === "created" && !error)) {
    return (
      <div className="panel" style={{ alignItems: "center", textAlign: "center", paddingBlock: 48 }}>
        <span className="eyebrow">Analizando</span>
        <h3 style={{ fontFamily: "var(--serif)", fontSize: 26 }}>Leyendo tu material</h3>
        <p style={{ color: "var(--muted)", maxWidth: 420 }}>Revisamos estilo, estructura, ritmo y ortografía. Puedes esperar aquí; no cierres esta pestaña.</p>
        <div className="meters" style={{ width: "100%", maxWidth: 360 }}>
          {[70, 85, 55].map((w, i) => (
            <div className="meter" key={`m-${w}-${i}`}><span style={{ color: "var(--muted)" }}>…</span><span className="bar"><i style={{ width: `${w}%`, transition: "width .8s" }} /></span><b> </b></div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !hasDraft) {
    return (
      <div className="panel" style={{ alignItems: "center", textAlign: "center", paddingBlock: 40, gap: 16 }}>
        <p style={{ color: "var(--proof)", maxWidth: 420 }}>{error}</p>
        <button className="btn btn-primary" onClick={runAnalyze}>Reintentar</button>
      </div>
    );
  }

  if (!hasDraft) {
    return (
      <div className="panel" style={{ alignItems: "center", paddingBlock: 40, gap: 16 }}>
        <p style={{ color: "var(--muted)" }}>Aún no hay borrador.</p>
        <button className="btn btn-primary" onClick={runAnalyze}>Generar borrador</button>
      </div>
    );
  }

  const words = chapters.reduce((a, c) => a + (c.contenido || "").trim().split(/\s+/).filter(Boolean).length, 0);
  const pages = Math.max(1, Math.round(words / 300));
  const metrics = project.metrics;
  const first = chapters[0];
  const firstBody = (first.contenido || "").trim();
  const dropChar = firstBody.charAt(0) || "—";

  return (
    <div className="panel">
      <div className="split wide">
        <div className="page-prev">
          <div className="seg" role="group" aria-label="Vista">
            <button type="button" data-testid="view-libro-btn" className={view === "libro" ? "on" : ""} onClick={() => setView("libro")}>Libro (muestra)</button>
            <button type="button" className={view === "pagina" ? "on" : ""} onClick={() => setView("pagina")}>Página</button>
            <button type="button" className={view === "texto" ? "on" : ""} onClick={() => setView("texto")}>Texto</button>
          </div>
          {view === "libro" ? (
            <div data-testid="draft-book-preview" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              {previewLoading && <p style={{ color: "var(--muted)", padding: 40 }}>Preparando la vista de tu libro…</p>}
              {previewError && (
                <div style={{ textAlign: "center", padding: 24 }}>
                  <p style={{ color: "var(--proof)" }}>{previewError}</p>
                  <button className="btn" onClick={() => { previewLoaded.current = false; loadPreview(); }}>Reintentar</button>
                </div>
              )}
              {previewUrl && !previewError && (
                <>
                  <iframe
                    data-testid="draft-preview-iframe"
                    title="Vista del libro (muestra)"
                    src={`${previewUrl}#view=FitH`}
                    style={{ width: "100%", maxWidth: 440, height: 560, border: "1px solid var(--rule)", borderRadius: 8, background: "#fff" }}
                  />
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer" data-testid="open-preview-link" style={{ fontSize: 13, color: "var(--prusia)", textDecoration: "underline" }}>Abrir la muestra en una pestaña nueva</a>
                  <small style={{ color: "var(--muted)", textAlign: "center" }}>Muestra página a página · portada, índice, capítulos y hojas del editor. El libro final, sin la marca «muestra», se entrega tras el pago.</small>
                </>
              )}
            </div>
          ) : view === "pagina" ? (
            <div className="bookpage" data-testid="draft-bookpage">
              <div style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted)", textAlign: "center" }}>Capítulo uno</div>
              <div data-testid="draft-title" style={{ fontFamily: "var(--serif)", fontSize: 24, fontStyle: "italic", textAlign: "center", lineHeight: 1.1 }}>{first.titulo}</div>
              <div style={{ width: 36, height: 1, background: "var(--ink)", margin: "2px auto 6px" }} />
              <p className="bp"><span className="drop">{dropChar}</span>{firstBody.slice(1, 480)}{firstBody.length > 480 ? "…" : ""}</p>
              <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", fontFamily: "var(--serif)", fontSize: 11, color: "var(--muted)" }}><span>{project.author_name}</span><span>1</span></div>
            </div>
          ) : (
            <div style={{ background: "#fff", border: "1px solid var(--rule)", borderRadius: 8, padding: 24, maxWidth: 420, maxHeight: 460, overflow: "auto", width: "100%" }}>
              {chapters.map((c, i) => (
                <div key={`${i}-${(c.titulo || "cap").slice(0, 24)}`} data-testid={`draft-chapter-${i}`} style={{ marginBottom: 18 }}>
                  <h4 style={{ fontFamily: "var(--serif)", fontSize: 18, color: "var(--prusia)" }}>{c.titulo}</h4>
                  <p style={{ fontFamily: "var(--serif)", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-line", marginTop: 6 }}>{c.contenido}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="stack">
          <div className="report">
            <div className="report-h">
              <span className="side-label" style={{ margin: 0 }}>Informe editorial · Revisión {project.revision_count || 0}</span>
            </div>
            <div className="rb" data-testid="feedback-report">
              <span className="lbl ok-l">Lectura de tu editor</span>
              <p>{project.informe_feedback}</p>
            </div>
            <div className="next"><strong>Título propuesto</strong><p style={{ fontFamily: "var(--serif)", fontSize: 18 }}>{project.titulo_sugerido || project.title}</p></div>
          </div>
          <div className="facts">
            <div><small>Capítulos</small><b className="num">{chapters.length}</b></div>
            <div><small>Palabras</small><b className="num">{words}</b></div>
            <div><small>Págs. est.</small><b className="num">{metrics?.estimated_pages || pages}</b></div>
            <div><small>Tamaño</small><b className="num" data-testid="draft-format" style={{ fontSize: 13, lineHeight: 1.25 }}>{project.book_format_label || "—"}</b></div>
          </div>
          {metrics && (
            <div data-testid="draft-measures" style={{ marginTop: 10, padding: "10px 14px", border: "1px solid var(--rule)", borderRadius: 10, display: "flex", flexWrap: "wrap", gap: "4px 18px", alignItems: "center", fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5 }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--prusia)" }}>Medidas de imprenta</span>
              <span>Página <b style={{ color: "var(--ink)", fontWeight: 600 }}>{metrics.page_cm}</b></span>
              <span>Margen interior <b style={{ color: "var(--ink)", fontWeight: 600 }}>{String(metrics.inside_cm).replace(".", ",")} cm</b></span>
              <span>Margen exterior <b style={{ color: "var(--ink)", fontWeight: 600 }}>{String(metrics.outside_cm).replace(".", ",")} cm</b></span>
            </div>
          )}
        </div>
      </div>

      <div className="panel-foot">
        <span style={{ color: "var(--muted)", fontSize: 14 }}>Borrador e informe listos.</span>
        <button type="button" data-testid="proceed-to-revisions-btn" className="btn btn-primary" onClick={onNext}>Ir a revisiones</button>
      </div>
    </div>
  );
}
